import { NavLink, useLocation } from 'react-router-dom';
import type { LucideIcon } from 'lucide-react';
import {
  LayoutDashboard,
  Building2,
  Users,
  UserCog,
  ListOrdered,
  Layers,
  Briefcase,
  Mail,
  CalendarCheck,
  MonitorPlay,
  Settings2,
  Sparkles,
  Home,
  ChevronDown,
  ChevronsLeft,
  ChevronsRight,
  BookOpen,
  BarChart2,
  Activity,
  Clock,
  Radar,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { usePermissions } from '@core/auth/usePermissions';
import { useAuth } from '@core/auth/useAuth';
import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { GGJLogoMark } from '@shared/components/Brand/GGJLogo';
import { SidebarFooter } from './SidebarFooter';
import { SidebarCollections } from './SidebarCollections';
import { SidebarTrialCard } from './SidebarTrialCard';

interface SidebarProps {
  open: boolean;
  onClose: () => void;
}

const SIDEBAR_COLLAPSED_LS_KEY = 'ggj.sidebar.collapsed';

function initialSidebarCollapsed(): boolean {
  if (typeof window === 'undefined') return false;
  if (window.innerWidth < 1024) return true;
  try {
    const raw = window.localStorage.getItem(SIDEBAR_COLLAPSED_LS_KEY);
    if (raw === 'true') return true;
    if (raw === 'false') return false;
  } catch {
    /* ignore storage errors */
  }
  return false;
}

export function Sidebar({ open, onClose }: SidebarProps) {
  const perms = usePermissions();
  const { user, isLoading: authLoading } = useAuth();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(initialSidebarCollapsed);
  const asideRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 1024) setCollapsed(true);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (window.innerWidth < 1024) return;
    try {
      window.localStorage.setItem(SIDEBAR_COLLAPSED_LS_KEY, collapsed ? 'true' : 'false');
    } catch {
      /* ignore storage errors */
    }
  }, [collapsed]);

  type NavItem = { to: string; label: string; icon: LucideIcon; show: boolean; matchPath?: (p: string) => boolean };
  type NavSection = { title: string; items: NavItem[]; accordion?: boolean };

  const { flatItems, accordionSections, settingsItem } = useMemo(() => {
    const flat: NavItem[] = [
      { to: '/home', label: 'Home', icon: Home, show: true },
      { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, show: true },
      { to: '/applications', label: 'Applications', icon: Layers, show: true },
      { to: '/smart-match', label: 'Smart Match', icon: Sparkles, show: true },
      { to: '/sourcing', label: 'Sourcing', icon: Radar, show: true },
      { to: '/library', label: 'Library', icon: BookOpen, show: true },
    ].filter((i) => i.show);

    const accordion: NavSection[] = [
      {
        title: 'People',
        accordion: true,
        items: [
          { to: '/candidates', label: 'Candidates', icon: Users, show: perms.canViewAllCandidates || perms.canCreateCandidate },
          { to: '/clients', label: 'Clients', icon: Building2, show: perms.canManageClients || perms.isJuniorRecruiter },
          { to: '/team', label: 'Team', icon: UserCog, show: perms.canManageTeam || perms.isJuniorRecruiter },
        ].filter((i) => i.show),
      },
      {
        title: 'Work',
        accordion: true,
        items: [
          { to: '/jobs', label: 'Jobs', icon: Briefcase, show: perms.canManageJobs || perms.isJuniorRecruiter },
          { to: '/requisitions', label: 'Requisitions', icon: ListOrdered, show: perms.canManageClients || perms.isJuniorRecruiter },
          { to: '/interviews', label: 'Interviews', icon: CalendarCheck, show: true },
          { to: '/ai-interviews', label: 'AI interviews', icon: MonitorPlay, show: true },
          { to: '/emails', label: 'Emails', icon: Mail, show: perms.canSendEmail || perms.canViewEmailActivity },
        ].filter((i) => i.show),
      },
      {
        title: 'Reports',
        accordion: true,
        items: [
          {
            to: '/reports',
            label: 'Overview',
            icon: BarChart2,
            show: true,
            matchPath: (p: string) => p === '/reports',
          },
          {
            to: '/reports/tat',
            label: 'Turn-around time',
            icon: Clock,
            show: true,
            matchPath: (p: string) => p.startsWith('/reports/tat'),
          },
          {
            to: '/reports/team',
            label: 'Team activity',
            icon: Activity,
            show: true,
            matchPath: (p: string) => p.startsWith('/reports/team'),
          },
        ].filter((i) => i.show),
      },
    ].filter((s) => s.items.length > 0);

    const settings: NavItem = {
      to: '/settings/profile',
      label: 'Settings',
      icon: Settings2,
      show: true,
      matchPath: (p) => p.startsWith('/settings'),
    };

    return { flatItems: flat, accordionSections: accordion, settingsItem: settings };
  }, [perms]);

  const [accordionOpen, setAccordionOpen] = useState<Record<string, boolean>>(() => {
    try {
      const raw = window.localStorage.getItem('ggj.sidebar.accordion');
      if (raw) return JSON.parse(raw) as Record<string, boolean>;
    } catch { /* ignore */ }
    return { People: true, Work: true, Reports: true };
  });

  const toggleAccordion = useCallback((title: string) => {
    setAccordionOpen((prev) => {
      const next = { ...prev, [title]: !prev[title] };
      try { window.localStorage.setItem('ggj.sidebar.accordion', JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
  }, []);

  // Phase 8 §3 A: expanded 196 px, collapsed 56 px.
  const widthClass = collapsed ? 'w-14' : 'w-[196px]';

  // Phase 8 §3 H: 30 px rows, 13 px text, 16 px icons, no left-accent border.
  const navItemClass = (isActive: boolean, isCollapsed: boolean) =>
    [
      'flex w-full items-center rounded-[8px] text-left text-[13px] font-medium transition-colors duration-150',
      isCollapsed ? 'justify-center px-0 py-3' : 'gap-2 px-2 h-[30px]',
      isActive
        ? 'vscode-bg-tertiary vscode-text-primary'
        : 'vscode-text-secondary hover:vscode-bg-hover hover:vscode-text-primary',
    ].join(' ');

  const orgLabel = user?.orgName?.trim() || '';

  return (
    <motion.aside
      ref={asideRef}
      initial={{ opacity: 0, x: -16 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
      className={`
        flex min-h-0 flex-col overflow-hidden
        border-r vscode-border
        transition-[width,transform] duration-200 ease-in-out
        fixed inset-y-0 left-0 z-30
        ${open ? 'translate-x-0' : '-translate-x-full'}
        lg:static lg:h-full lg:max-h-full lg:min-h-0 lg:self-stretch lg:translate-x-0
        ${widthClass}
      `}
      style={{ backgroundColor: 'var(--ggj-sidebar-bg)' }}
    >
      {/* ── Top row: workspace chip (left) + collapse chevron (right) ── */}
      <div className={`flex flex-shrink-0 items-center gap-1 px-2 pt-3 pb-2 ${collapsed ? 'justify-center' : ''}`}>
        {collapsed ? (
          <button
            type="button"
            onClick={() => setCollapsed(false)}
            className="flex items-center justify-center rounded-[8px] p-1.5 outline-none transition-colors hover:vscode-bg-hover"
            aria-label="Expand sidebar"
            title="Expand sidebar"
          >
            <GGJLogoMark size={24} className="flex-shrink-0" />
          </button>
        ) : (
          <>
            <button
              type="button"
              onClick={onClose}
              className="flex min-w-0 flex-1 items-center gap-2 rounded-[8px] px-1.5 py-1.5 text-left transition-colors hover:vscode-bg-hover group"
              aria-label="Workspace menu"
              title={orgLabel || 'Workspace'}
            >
              <span
                className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-[6px]"
                style={{ backgroundColor: 'color-mix(in srgb, var(--ggj-accent) 20%, transparent)' }}
                aria-hidden
              >
                <GGJLogoMark size={16} />
              </span>
              {authLoading ? (
                <span className="block h-3 w-24 animate-pulse rounded vscode-bg-tertiary" />
              ) : (
                <span className="min-w-0 flex-1 truncate text-[13px] font-semibold" style={{ color: 'var(--ggj-sidebar-text-active)' }}>
                  {orgLabel || 'Workspace'}
                </span>
              )}
              <ChevronDown className="h-3.5 w-3.5 flex-shrink-0 opacity-0 transition-opacity group-hover:opacity-60" aria-hidden />
            </button>
            <button
              type="button"
              onClick={() => setCollapsed(true)}
              className="hidden lg:flex flex-shrink-0 rounded-[8px] p-1.5 vscode-text-tertiary transition-colors hover:vscode-bg-hover hover:vscode-text-primary"
              aria-label="Collapse sidebar"
              title="Collapse sidebar"
            >
              <ChevronsLeft className="h-4 w-4" aria-hidden />
            </button>
          </>
        )}
      </div>

      {/* Command Palette is keyboard-only: ⌘K opens Find mode.
          The AI prompt lives inside /smart-match/new — never in the sidebar. */}

      <nav
        className={`min-h-0 flex-1 overflow-y-auto overflow-x-hidden overscroll-y-contain [scrollbar-gutter:stable] flex flex-col ${collapsed ? 'px-2 py-2 gap-1' : 'px-3 pb-2 gap-0.5'}`}
        aria-label="Main navigation"
      >
        {collapsed ? (
          /* ── COLLAPSED (icon rail): flat icon list, no section headers ── */
          <div className="flex min-h-full flex-col">
            <div className="flex flex-col">
              {[
                ...flatItems,
                ...accordionSections.flatMap((s) => s.items),
              ].map(({ to, label, icon: Icon, matchPath }) => {
                const isItemActive = matchPath ? matchPath(location.pathname) : undefined;
                return (
                  <div key={to}>
                    <NavLink
                      to={to}
                      onClick={onClose}
                      title={label}
                      aria-current={isItemActive ? 'page' : undefined}
                      className={({ isActive: navIsActive }) =>
                        navItemClass(matchPath ? matchPath(location.pathname) : navIsActive, true)
                      }
                    >
                      {() => <Icon className="h-4 w-4 flex-shrink-0" aria-hidden />}
                    </NavLink>
                  </div>
                );
              })}
            </div>
            <div className="mt-auto border-t border-[color-mix(in_srgb,var(--vscode-border-primary)_40%,transparent)] pt-2">
              <NavLink
                to={settingsItem.to}
                title={settingsItem.label}
                aria-current={settingsItem.matchPath ? (settingsItem.matchPath(location.pathname) ? 'page' : undefined) : undefined}
                className={({ isActive: navIsActive }) =>
                  navItemClass(
                    settingsItem.matchPath ? settingsItem.matchPath(location.pathname) : navIsActive,
                    true,
                  )
                }
              >
                {() => <settingsItem.icon className="h-4 w-4 flex-shrink-0" aria-hidden />}
              </NavLink>
            </div>
          </div>
        ) : (
          /* ── EXPANDED: flat main items + accordion groups + Collections ── */
          <>
            {/* Flat main items (no MAIN label). Phase 8 §21.C/§26.B: the
                Smart Match recent-search chip renders immediately above the
                Dashboard nav item when a recent thread exists. */}
            {flatItems.map(({ to, label, icon: Icon, matchPath }) => {
              const isItemActive = matchPath ? matchPath(location.pathname) : undefined;
              return (
                <div key={to}>
                  <NavLink
                    to={to}
                    onClick={onClose}
                    aria-current={isItemActive ? 'page' : undefined}
                    className={({ isActive: navIsActive }) =>
                      navItemClass(matchPath ? matchPath(location.pathname) : navIsActive, false)
                    }
                  >
                    {() => (
                      <>
                        <Icon className="h-4 w-4 flex-shrink-0" aria-hidden />
                        <span className="truncate">{label}</span>
                      </>
                    )}
                  </NavLink>
                </div>
              );
            })}

            {/* People / Work accordions */}
            {accordionSections.map((section) => {
              const isOpen = accordionOpen[section.title] ?? true;
              return (
                <div key={section.title} className="mt-2">
                  <button
                    type="button"
                    onClick={() => toggleAccordion(section.title)}
                    className="flex w-full items-center justify-between rounded-md px-2 py-1 text-[11px] font-semibold uppercase tracking-[0.06em] vscode-text-tertiary hover:vscode-bg-hover hover:vscode-text-primary transition-colors"
                    aria-expanded={isOpen}
                  >
                    <span>{section.title}</span>
                    <ChevronDown
                      className={`h-3 w-3 flex-shrink-0 transition-transform duration-200 ${
                        isOpen ? 'rotate-0' : '-rotate-90'
                      }`}
                      aria-hidden
                    />
                  </button>
                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        key="items"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.18, ease: [0.22, 1, 0.36, 1] }}
                        className="overflow-hidden"
                      >
                        <div className="mt-1 flex flex-col gap-0.5">
                          {section.items.map(({ to, label, icon: Icon, matchPath }) => {
                            const isItemActive = matchPath ? matchPath(location.pathname) : undefined;
                            return (
                              <NavLink
                                key={to}
                                to={to}
                                onClick={onClose}
                                aria-current={isItemActive ? 'page' : undefined}
                                className={({ isActive: navIsActive }) =>
                                  navItemClass(matchPath ? matchPath(location.pathname) : navIsActive, false)
                                }
                              >
                                {() => (
                                  <>
                                    <Icon className="h-4 w-4 flex-shrink-0" aria-hidden />
                                    <span className="truncate">{label}</span>
                                  </>
                                )}
                              </NavLink>
                            );
                          })}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}

            {/* Settings standalone */}
            <div className="mt-2">
              <NavLink
                to={settingsItem.to}
                onClick={onClose}
                aria-current={settingsItem.matchPath ? (settingsItem.matchPath(location.pathname) ? 'page' : undefined) : undefined}
                className={({ isActive: navIsActive }) =>
                  navItemClass(
                    settingsItem.matchPath ? settingsItem.matchPath(location.pathname) : navIsActive,
                    false,
                  )
                }
              >
                {() => (
                  <>
                    <settingsItem.icon className="h-4 w-4 flex-shrink-0" aria-hidden />
                    <span className="truncate">{settingsItem.label}</span>
                  </>
                )}
              </NavLink>
            </div>
          </>
        )}
      </nav>

      {/* Collections + trial card only when expanded */}
      {!collapsed && (
        <>
          <SidebarCollections />
          <div className="mt-3">
            <SidebarTrialCard />
          </div>
        </>
      )}

      {/* Collapse chevron for collapsed state also appears in the icon rail above */}
      {collapsed && (
        <div className="flex-shrink-0 border-t border-[color-mix(in_srgb,var(--vscode-border-primary)_40%,transparent)] px-2 py-2">
          <button
            type="button"
            onClick={() => setCollapsed(false)}
            className="hidden lg:flex w-full items-center justify-center rounded-[8px] p-2 vscode-text-tertiary transition-colors hover:vscode-bg-hover hover:vscode-text-primary"
            aria-label="Expand sidebar"
            title="Expand sidebar"
          >
            <ChevronsRight className="h-4 w-4" aria-hidden />
          </button>
        </div>
      )}

      <SidebarFooter collapsed={collapsed} />
    </motion.aside>
  );
}
